/**
 * Copyright (c) 2025-present, NDLA.
 *
 * This source code is licensed under the GPLv3 license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { gql } from "@apollo/client";
import { transform } from "@ndla/article-converter";
import { getLicenseByAbbreviation } from "@ndla/licenses";
import { Heading } from "@ndla/primitives";
import { ArticleWrapper, ArticleContent, ArticleHeader, LicenseLink } from "@ndla/ui";
import { useTranslation } from "react-i18next";
import type { GQLLearningpathStepTitle_LearningpathStepFragment } from "../../../graphqlTypes";
import { InactiveMessageBox } from "../../InactiveMessageBox";
import { ResourceContent } from "../../Resource/ResourceLayout";

interface Props {
  isInactive?: boolean;
  learningpathStep: GQLLearningpathStepTitle_LearningpathStepFragment;
  skipToContentId?: string;
}

export const LearningpathStepTitle = ({ learningpathStep, skipToContentId, isInactive }: Props) => {
  const { i18n } = useTranslation();
  return learningpathStep.showTitle || learningpathStep.description ? (
    <ResourceContent variant="content">
      <ArticleWrapper>
        {!!learningpathStep.showTitle && (
          <ArticleHeader>
            <Heading id={learningpathStep.showTitle ? skipToContentId : undefined}>{learningpathStep.title}</Heading>
            <LicenseLink
              license={getLicenseByAbbreviation(learningpathStep.copyright?.license?.license ?? "", i18n.language)}
            />
            {!!isInactive && <InactiveMessageBox />}
          </ArticleHeader>
        )}
        <ArticleContent>
          {!!learningpathStep.description && <section>{transform(learningpathStep.description, {})}</section>}
        </ArticleContent>
      </ArticleWrapper>
    </ResourceContent>
  ) : null;
};

LearningpathStepTitle.fragments = {
  learningpathStep: gql`
    fragment LearningpathStepTitle_LearningpathStep on BaseLearningpathStep {
      id
      showTitle
      description
      title
      copyright {
        license {
          license
        }
      }
    }
  `,
};
